import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import api from "api/api";
import Select from "react-select";

// Material UI Components
import Button from "@mui/material/Button";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

// import mui icons
import EditIcon from "@mui/icons-material/Edit";

// Styles
import ReactSelectStyles from "../../../styles/ReactSelectStyles";

// import components
import Loader from "../../common/Loader";
import AddUpdateComponent from "./AddUpdateComponent";
import DeleteComponent from "./DeleteComponent";
import ImportMappingComponent from "./ImportMappingComponent";

// other packages
import { AgGridReact } from "ag-grid-react";

// redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import { loadIsDataMappingAcrossStudies, loadCentralizedDataMappingSelectedStudyTypeState, loadCentralizedDataMappingSelectedStudyTypeMethodState } from "store/features/dataControlSlice";
import DataViewSelector from "components/claimsMapping/DataViewSelector";
// Import utility functions
import {
    transformStudyTypeOptions,
    transformStudyTypeMethodOptions,
    formatStudyTypeLabel,
    formatMethodLabel,
    getStudyTypeMethodSelectStyles,
    getInfoMessageOption,
    handleTemplateBasedMethodSelection
} from "../../../utils/studyTypeUtils";

function CentralizedDataMapping() {
    const gridRef = useRef();
    const dispatch = useDispatch();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // store data
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const [studyTypeOptions, setStudyTypeOptions] = useState([]);
    const [studyTypeMethodOptions, setStudyTypeMethodOptions] = useState([]);
    const [selectedStudyType, setSelectedStudyType] = useState(null);
    const [selectedStudyTypeMethod, setSelectedStudyTypeMethod] = useState([]);

    // const [allData, setAllData] = useState([]);
    const [dataView, setDataView] = useState({ value: 1, label: "Data Mapping Across Studies" });

    // dialog data
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedRowToUpdate, setSelectedRowToUpdate] = useState(null);

    const dataViewOptions = [
        { value: 1, label: "Data Mapping Across Studies" },
        { value: 0, label: "Data Mapping Not Present In Any Study" },
    ];

    const buttonStyles = {
        height: "30px",
        minWidth: "auto",
        padding: "6px 12px",
        fontSize: "12px",
        textTransform: "none",
        border: "1px solid #1976d2cc",
    };

    const optionsWithInfoMessage = useMemo(() => {
        if (studyTypeMethodOptions.length === 0) return [];
        const infoMessage = getInfoMessageOption();
        return [infoMessage, ...studyTypeMethodOptions];
    }, [studyTypeMethodOptions]);

    // Custom styles for multi-select
    const customMethodReactSelectStyles = useMemo(
        () => getStudyTypeMethodSelectStyles(ReactSelectStyles),
        []
    );

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const fetchData = useCallback(async () => {
        dispatch(loadIsDataMappingAcrossStudies(dataView.value == 1));
        if (productCategoryId && selectedStudyType?.value && selectedStudyTypeMethod && selectedStudyTypeMethod.length > 0) {
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/all/`;
                url += `?product_category=${productCategoryId}`;
                url += `&study_type=${selectedStudyType.value}`;
                const methodIds = selectedStudyTypeMethod.map(m => m.value).join(',');
                url += `&study_type_method=${methodIds}`;
                url += `&nomadic=${dataView.value === 0 ? "true" : "false"}`;
                setIsLoading(true);
                await api
                    .get(url)
                    .then((res) => {
                        // setAllData(res.data);
                        setData(res.data);
                        setSelectedRows([]);
                        setIsLoading(false);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    }, [productCategoryId, selectedStudyType?.value, selectedStudyTypeMethod, handleError, dataView.value, dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const fetchStudyTypes = async () => {
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/study-types/all/`;
                if (productCategoryId) {
                    url += `?product_category=${productCategoryId}`;
                }
                await api
                    .get(url)
                    .then((res) => {
                        const options = transformStudyTypeOptions(res.data);
                        setStudyTypeOptions(options);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        };

        fetchStudyTypes();
    }, [productCategoryId, handleError]);

    // Fetch study type methods when study type changes
    useEffect(() => {
        const fetchStudyTypeMethods = async () => {
            try {
                // setAllData([]);
                setData([]);
                let url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/all/`;
                if (productCategoryId) {
                    url += `?product_category=${productCategoryId}`;
                }
                // If study type is selected, filter methods by it
                if (selectedStudyType?.value) {
                    url += `&study_type=${selectedStudyType.value}`;
                }

                await api
                    .get(url)
                    .then((res) => {
                        const options = transformStudyTypeMethodOptions(res.data);
                        setStudyTypeMethodOptions(options);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        };

        fetchStudyTypeMethods();
    }, [selectedStudyType, productCategoryId, handleError]);

    // useEffect(() => {
    //     let _allRows = [...allData];
    //     const isDataMappingAcrossStudies = dataView.value === 1;
    //     if (dataView.value === 0) {
    //         // Filter to show only mapping present in any study
    //         const filteredData = _allRows.filter((item) => item?.core_data_model_mapping.length === 0) || [];
    //         _allRows = [...filteredData];
    //     } else {
    //         const filteredData = _allRows.filter((item) => item?.core_data_model_mapping.length !== 0) || [];
    //         _allRows = [...filteredData];
    //     }
    //     // Dispatching action to update isDataMappingAcrossStudies state
    //     dispatch(loadIsDataMappingAcrossStudies(isDataMappingAcrossStudies));

    //     setData([..._allRows]);
    //     setSelectedRows([]);
    // }, [allData, dataView, dispatch]);

    const handleDelete = () => {
        const selectedIds = selectedRows.map((datum) => datum.id);
        const payload = { ids: selectedIds };
        try {
            const url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/batch-delete/`;
            api.put(url, payload)
                .then(() => {
                    fetchData();
                    setIsDeleteDialogOpen(false);
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };

    const onSelectionChanged = useCallback(() => {
        if (gridRef.current) {
            const selectedNodes = gridRef.current.api.getSelectedNodes();
            const selectedData = selectedNodes.map((node) => node.data);
            setSelectedRows(selectedData);
        }
    }, []);

    const handleChange = (selectedValue, type, actionMeta) => {
        if (type === "studyType") {
            setSelectedStudyType(selectedValue);
            dispatch(loadCentralizedDataMappingSelectedStudyTypeState(selectedValue));
            if (selectedValue && (!selectedStudyTypeMethod.length || selectedStudyTypeMethod[0]?.studyTypeValue !== selectedValue.value)) {
                setSelectedStudyTypeMethod([]);
                dispatch(loadCentralizedDataMappingSelectedStudyTypeMethodState([]));
            }
        } else if (type === "studyTypeMethod") {
            const updatedSelection = handleTemplateBasedMethodSelection(
                selectedStudyTypeMethod,
                actionMeta,
                studyTypeMethodOptions
            );
            setSelectedStudyTypeMethod(updatedSelection);
            dispatch(loadCentralizedDataMappingSelectedStudyTypeMethodState(updatedSelection));
        }
    };

    const editCellRenderer = (row) => {
        return (
            <EditIcon
                color="primary"
                style={{
                    cursor: "pointer",
                }}
                onClick={() => {
                    setSelectedRowToUpdate(row.data);
                    setIsUpdateDialogOpen(true);
                }}
            />
        );
    };

    const columnDefs = [
        {
            headerName: "Backend Variable",
            minWidth: 250,
            field: "backend_variable",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
        },
        {
            headerName: "Variable Product Performance Macro-Category",
            minWidth: 300,
            field: "macro_category",
        },
        {
            headerName: "Variable Category",
            minWidth: 250,
            field: "variable_category",
        },
        {
            headerName: "Display Label",
            minWidth: 250,
            field: "display_label",
        },
        {
            headerName: "Attribute",
            minWidth: 250,
            field: "attribute",
        },
        {
            headerName: "Attribute Value",
            minWidth: 250,
            field: "attribute_value",
        },
        {
            headerName: "Data Type",
            minWidth: 200,
            field: "data_type",
        },
        {
            headerName: "Type of Analysis",
            minWidth: 200,
            field: "type_of_analysis",
            hide: isUnileverFragranceHouse,
        },
        {
            headerName: "Action",
            minWidth: 120,
            filter: false,
            cellRenderer: editCellRenderer,
        },
    ];

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            suppressMenu: true,
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellStyle: { fontSize: "12px", border: "none" },
            minWidth: 490,
        };
    }, []);

    return (
        <div>
            {isLoading && <Loader />}
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "30px",
                    padding: "0 15px",
                    gap: "15px",
                    flexWrap: "wrap",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <div style={{ width: "200px", minWidth: "150px" }}>
                        <Select
                            options={studyTypeOptions}
                            value={selectedStudyType}
                            onChange={(newValue) => handleChange(newValue, "studyType")}
                            formatOptionLabel={formatStudyTypeLabel}
                            styles={ReactSelectStyles}
                            placeholder="Study Type"
                            isClearable
                        />
                    </div>

                    <div style={{ width: "300px", minWidth: "400px" }}>
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={optionsWithInfoMessage}
                            styles={customMethodReactSelectStyles}
                            value={selectedStudyTypeMethod}
                            onChange={(newValue, actionMeta) => handleChange(newValue, "studyTypeMethod", actionMeta)}
                            formatOptionLabel={formatMethodLabel}
                            placeholder="Study Type Method"
                            isClearable
                        />
                    </div>

                    <div style={{ flex: 1, minWidth: "20px" }}></div>

                    <div style={{ width: "200px", minWidth: "250px" }}>
                        <DataViewSelector
                            value={dataView}
                            setValue={setDataView}
                            options={dataViewOptions}
                        />
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        height: "30px",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<ControlPointOutlinedIcon sx={{ fontSize: 16 }} />}
                        style={
                            !selectedStudyType || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0
                                ? {
                                      ...buttonStyles,
                                      color: "rgba(0, 0, 0, 0.26)",
                                      borderColor: "rgba(0, 0, 0, 0.12)",
                                  }
                                : {
                                      ...buttonStyles,
                                      color: "#1976d2",
                                  }
                        }
                        onClick={() => setIsAddDialogOpen(true)}
                        disabled={!selectedStudyType || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0}
                    >
                        Add
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DeleteOutlinedIcon sx={{ fontSize: 16 }} />}
                        style={
                            selectedRows.length === 0 || dataView.value === 1
                                ? {
                                      ...buttonStyles,
                                      color: "rgba(0, 0, 0, 0.26)",
                                      borderColor: "rgba(0, 0, 0, 0.12)",
                                  }
                                : {
                                      ...buttonStyles,
                                      color: "#d32f2f",
                                      borderColor: "#d32f2f",
                                  }
                        }
                        onClick={() => setIsDeleteDialogOpen(true)}
                        disabled={selectedRows.length === 0 || dataView.value === 1}
                    >
                        Delete
                    </Button>
                    <ImportMappingComponent
                        studyTypeOptions={studyTypeOptions}
                        isDisabled={!selectedStudyType || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                        style={
                            !selectedStudyType || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0
                                ? {
                                      ...buttonStyles,
                                      color: "rgba(0, 0, 0, 0.26)",
                                      borderColor: "rgba(0, 0, 0, 0.12)",
                                  }
                                : buttonStyles
                        }
                        onClick={() => {
                            let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/download-template/?blank_template=true`;
                            window.open(url, "_blank");
                        }}
                        disabled={!selectedStudyType || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0}
                    >
                        Export
                    </Button>
                </div>
            </div>
            <div className="landing-page-table mb-0">
                <div
                    className="ag-theme-alpine"
                    style={{ height: "calc(100vh - 310px)", width: "100%" }}
                >
                    <AgGridReact
                        ref={gridRef}
                        rowSelection="multiple"
                        rowMultiSelectWithClick
                        rowStyle={{
                            background: "white",
                        }}
                        defaultColDef={defaultColDef}
                        rowData={data}
                        columnDefs={columnDefs}
                        onGridReady={(params) => params.id}
                        onSelectionChanged={onSelectionChanged}
                        rowHeight={38}
                        headerHeight={40}
                        suppressDragLeaveHidesColumns={true}
                        sortingOrder={sortingOrder}
                        suppressRowClickSelection={true}
                    ></AgGridReact>
                </div>
                <div className="d-flex">
                    <span
                        style={{
                            fontSize: 12,
                            color: "#495057",
                            marginRight: 20,
                            fontWeight: 600,
                        }}
                    >
                        Selected rows: {selectedRows.length}
                    </span>
                    <span
                        style={{
                            fontSize: 12,
                            color: "#495057",
                            fontWeight: 600,
                        }}
                    >
                        Total rows: {data.length}
                    </span>
                </div>
            </div>
            {isAddDialogOpen && (
                <AddUpdateComponent
                    title="Add Centralized Data Mapping Record"
                    type="add"
                    fetchData={fetchData}
                    isOpen={isAddDialogOpen}
                    setIsAddDialogOpen={setIsAddDialogOpen}
                    selectedStudyType={selectedStudyType}
                    selectedStudyTypeMethod={selectedStudyTypeMethod}
                />
            )}
            {isUpdateDialogOpen && (
                <AddUpdateComponent
                    title="Update Centralized Data Mapping Record"
                    type="update"
                    selectedRow={selectedRowToUpdate}
                    fetchData={fetchData}
                    dataView={dataView}
                    isOpen={isUpdateDialogOpen}
                    setIsUpdateDialogOpen={setIsUpdateDialogOpen}
                    selectedStudyType={selectedStudyType}
                    selectedStudyTypeMethod={selectedStudyTypeMethod}
                />
            )}
            {isDeleteDialogOpen && (
                <DeleteComponent
                    isDeleteDialogOpen={isDeleteDialogOpen}
                    setIsDeleteDialogOpen={setIsDeleteDialogOpen}
                    selectedRows={selectedRows}
                    handleDelete={handleDelete}
                />
            )}
        </div>
    );
}

export default CentralizedDataMapping;
